import { fromJS } from 'immutable';
import Segment from '../../helpers/segment';
import { JobsActionTypes } from './actions';
import { createReducer } from '../../helpers/store';

const initialState = fromJS({
  errors: '',
  isLoading: false,
  jobs: [],
  job: {}
});

/**
 * Called when get job request is initiated.
 *
 * @param {Map} state
 * @param {Object} action
 * @returns {Map}
 */

export const handleClearJob = (state, action) => {
  Segment.track('Clearing job');
  return state.set('job', fromJS({}));
};

export const handleGetJobRequest = (state, action) => {
  Segment.track('Getting job');
  return state.set('isLoading', true).set('errors', '');
};

/**
 * Called when the get job request fails.
 *
 * @param {Map} state
 * @param {Object} action
 * @returns {Map}
 */

export const handleGetJobFailure = (state, action) => {
  Segment.track('Failed to get job', { error: action.data.error });
  return state
    .set('isLoading', false)
    .set(
      'errors',
      action.data.error || 'Sorry, we were unable to load your job.'
    );
};

/**
 * Called when get job request is successful.
 *
 * @param {Map} state
 * @param {Object} action
 * @returns {Map}
 */

export const handleGetJobSuccess = (state, action) => {
  Segment.track('Got job');
  return state
    .set('errors', '')
    .set('job', fromJS(action.response.data))
    .set('isLoading', false);
};

/**
 * Called when get jobs request is initiated.
 *
 * @param {Map} state
 * @param {Object} action
 * @returns {Map}
 */

export const handleGetJobsRequest = (state, action) => {
  Segment.track('Getting jobs');
  return state.set('isLoading', true).set('errors', '');
};

/**
 * Called when the get jobs request fails.
 *
 * @param {Map} state
 * @param {Object} action
 * @returns {Map}
 */

export const handleGetJobsFailure = (state, action) => {
  Segment.track('Failed to get jobs', { error: action.data.error });
  return state
    .set('isLoading', false)
    .set(
      'errors',
      action.data.error || 'Sorry, we were unable to load your jobs.'
    );
};

/**
 * Called when get jobs request is successful.
 *
 * @param {Map} state
 * @param {Object} action
 * @returns {Map}
 */

export const handleGetJobsSuccess = (state, action) => {
  Segment.track('Got jobs');
  return state
    .set('errors', '')
    .set('jobs', fromJS(action.response.data))
    .set('isLoading', false);
};

/**
 * Called when create job request is initiated.
 *
 * @param {Map} state
 * @param {Object} action
 * @returns {Map}
 */

export const handleCreateJobRequest = (state, action) => {
  Segment.track('Creating job');
  return state.set('errors', '').set('isLoading', true);
};

/**
 * Called when the create job request fails.
 *
 * @param {Map} state
 * @param {Object} action
 * @returns {Map}
 */
export const handleCreateJobFailure = (state, action) => {
  Segment.track('Failed to create job', { error: action.error });
  return state
    .set(
      'errors',
      action.error.data.error || 'Sorry, we were unable to create your job.'
    )
    .set('isLoading', false);
};

/**
 * Called when create job request is successful.
 *
 * @param {Map} state
 * @param {Object} action
 * @returns {Map}
 */
export const handleCreateJobSuccess = (state, action) => {
  Segment.track('Created job');
  return state
    .set('errors', '')
    .set('showModal', false)
    .set('isLoading', false);
};

export const handleUpdateJobRequest = (state, action) => {
  Segment.track('Updating job');
  return state.set('errors', '').set('isLoading', true);
};

/**
 * Called when the create job request fails.
 *
 * @param {Map} state
 * @param {Object} action
 * @returns {Map}
 */
export const handleUpdateJobFailure = (state, action) => {
  Segment.track('Failed to update job', { error: action.error });
  return state
    .set('errors', 'Sorry, we were unable to update your job.')
    .set('isLoading', false);
};

/**
 * Called when create job request is successful.
 *
 * @param {Map} state
 * @param {Object} action
 * @returns {Map}
 */
export const handleUpdateJobSuccess = (state, action) => {
  Segment.track('Updated job');
  return state
    .set('errors', '')
    .set('showModal', false)
    .set('isLoading', false);
};

export const handleDeleteJobRequest = (state, action) => {
  Segment.track('deleting job');
  return state.set('errors', '');
};

/**
 * Called when the Delete job request fails.
 *
 * @param {Map} state
 * @param {Object} action
 * @returns {Map}
 */

export const handleDeleteJobFailure = (state, action) => {
  Segment.track('Failed to Delete job', { error: '' });
  return state
    .set('isLoading', false)
    .set(
      'errors',
      action.error.data.error || 'Sorry, we were unable to delete your job.'
    );
};

/**
 * Called when get job request is successful.
 *
 * @param {Map} state
 * @param {Object} action
 * @returns {Map}
 */

export const handleDeleteJobSuccess = (state, action) => {
  Segment.track('Delete job');
  const job_id = action.payload.id;

  const jobs = state.get('jobs').filter(job => {
    return parseInt(job.get('id'), 10) !== job_id;
  });
  return state
    .set('jobs', jobs)
    .set('errors', '')
    .set('isLoading', false);
};

export const jobsReducer = createReducer(initialState, {
  [JobsActionTypes.CLEAR_JOB]: handleClearJob,

  [JobsActionTypes.CREATE_JOB_REQUEST]: handleCreateJobRequest,
  [JobsActionTypes.CREATE_JOB_SUCCESS]: handleCreateJobSuccess,
  [JobsActionTypes.CREATE_JOB_FAILURE]: handleCreateJobFailure,

  [JobsActionTypes.UPDATE_JOB_REQUEST]: handleUpdateJobRequest,
  [JobsActionTypes.UPDATE_JOB_SUCCESS]: handleUpdateJobSuccess,
  [JobsActionTypes.UPDATE_JOB_FAILURE]: handleUpdateJobFailure,

  [JobsActionTypes.DELETE_JOB_REQUEST]: handleDeleteJobRequest,
  [JobsActionTypes.DELETE_JOB_SUCCESS]: handleDeleteJobSuccess,
  [JobsActionTypes.DELETE_JOB_FAILURE]: handleDeleteJobFailure,

  [JobsActionTypes.GET_JOB_REQUEST]: handleGetJobRequest,
  [JobsActionTypes.GET_JOB_SUCCESS]: handleGetJobSuccess,
  [JobsActionTypes.GET_JOB_FAILURE]: handleGetJobFailure,

  [JobsActionTypes.GET_JOBS_REQUEST]: handleGetJobsRequest,
  [JobsActionTypes.GET_JOBS_SUCCESS]: handleGetJobsSuccess,
  [JobsActionTypes.GET_JOBS_FAILURE]: handleGetJobsFailure
});



/** WEBPACK FOOTER **
 ** ./src/state/jobs/reducer.js
 **/